import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Search, X, MessageSquare } from 'lucide-react';
import { AppShell, type NavSection } from '@solid/ui';
import { SearchDropdown } from '@/components/ui/SearchDropdown';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { ProjectNav } from './ProjectNav';
import { AppBarFallback } from './AppBarItems';
import { FavoritesPanel } from './FavoritesPanel';

/**
 * No global nav sections: the sidebar is the project nav (ProjectNav), so
 * every screen outside it names itself here. Unlisted routes print the brand.
 */
const NO_SECTIONS: NavSection[] = [];
const TITLES: Array<[string, string]> = [
  ['/projects', 'Projects'],
  ['/activity', 'Activity'],
  ['/tasks', 'My tasks'],
  ['/announcements', 'Announcements'],
  ['/forms', 'Forms'],
  ['/organizations', 'Organizations'],
  ['/settings', 'Settings'],
  ['/help', 'Support'],
  ['/dashboard', 'Dashboard'],
];

/**
 * The one page frame, from @solid/ui — the same shell sales, monitoring and
 * the accounts portal wear.
 *
 * On the app rail: the rail script sets `body { margin-left }`, which the
 * shell's sticky, in-flow sidebar inherits. So unlike the old fixed sidebar,
 * nothing here adds a rail offset — doing so would double it. That also means
 * IS_STANDALONE needs no special case: no rail, no body margin, no offset.
 */
export function AppLayout() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  /**
   * ON A PHONE THE SEARCH IS AN ICON UNTIL ASKED FOR.
   *
   * The box ate most of the header, leaving the project name as 'Proje…'
   * (Jesse, 2026-08-28). Tapping the icon gives search the whole row — which
   * is what you want the moment you are actually searching — and closing it
   * gives the row back. Desktop keeps the box, always.
   */
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const primaryOrg = user?.organizations?.[0];

  // Project pages carry their own header and full-bleed layout (ProjectLayout),
  // so the shell's content padding would double up on them.
  const isProjectPage = location.pathname.startsWith('/projects/');

  return (
    <AppShell
      brand={{ name: 'Solid Hub', mark: <MessageSquare /> }}
      sections={NO_SECTIONS}
      offNavTitles={TITLES}
      sidebarExtra={({ onNavigate }) => <ProjectNav onNavigate={onNavigate} />}
      topbarEnd={
        <div className="flex w-full items-center justify-end gap-1">
        {/* Favorites and Activity live on the app bar; without one (standalone
            dev) they'd have no way in, so they sit here instead. */}
        <AppBarFallback />
        <div className={cn('relative w-full max-w-md', !mobileSearchOpen && 'w-auto sm:w-full')}>
          {/* Phone, closed: just the magnifier. */}
          {!mobileSearchOpen && (
            <button
              type="button"
              onClick={() => setMobileSearchOpen(true)}
              aria-label="Search the hub"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-grey-500 transition-colors hover:bg-grey-100 hover:text-secondary sm:hidden"
            >
              <Search className="h-5 w-5" />
            </button>
          )}
          <div className={cn('group relative', !mobileSearchOpen && 'hidden sm:block')}>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-grey-400 transition-colors group-focus-within:text-accent" />
            <input
              type="search"
              data-testid="topbar-search"
              aria-label="Search the hub"
              placeholder="Search…"
              // Opening the icon and then having to tap the box is two taps
              // for one intention.
              autoFocus={mobileSearchOpen}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(e.target.value.length >= 2);
              }}
              onFocus={() => {
                if (searchQuery.length >= 2) setSearchOpen(true);
              }}
              className="h-9 w-full rounded-lg border border-grey-200 bg-white pl-9 pr-9 text-sm shadow-sm transition-all placeholder:text-grey-400 hover:border-grey-300 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            {/* The X clears on desktop; on a phone an empty box also CLOSES,
                giving the header row back to the page title. */}
            {(searchQuery || mobileSearchOpen) && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSearchOpen(false);
                  setMobileSearchOpen(false);
                }}
                aria-label={searchQuery ? 'Clear search' : 'Close search'}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <SearchDropdown
            query={searchQuery}
            isOpen={searchOpen}
            onClose={() => setSearchOpen(false)}
            organizationId={primaryOrg?.id}
          />
        </div>
        </div>
      }
      contentMaxWidth={isProjectPage ? '100%' : 1600}
    >
      <Outlet />
      <FavoritesPanel />
    </AppShell>
  );
}
